import React, { useEffect, useRef, useState } from "react"
import Sidebar from "../../Components/Sidebar/Sidebar"
import NumberInput from "../../Components/Commons/Forms/NumberInput"
import { Radio, DatePicker } from "antd"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import dayjs from "dayjs"
import "./Patients.css"
import { createVital, createOBProfile } from "../Helpers/API/VitalSignsApi/VitalSignsApi"
import { DATE_FORMAT } from "../../Utils/dateFormat";
import BtnAdd from "../Commons/Buttons/BtnAdd"
import TextArea from "../Commons/Forms/TextArea"
import { formatDate, getRole, getUser } from "../Helpers/Utilities/Common"
import DateField from "../Commons/Forms/DateField"
import TextField from "../Commons/Forms/TextField"
import CreateSelectField from "../Commons/Forms/CreateSelectField"
import { getAutoSuggestTherapeutics } from "../Helpers/API/CheckUpApi/CheckUpApi"

import { getPatientID } from "../Helpers/API/PatientApi/PatientApi"

function NewPatientVitalSigns() {
  const id = useParams()
  const role = getRole()
  const userId = getUser()
  const navigate = useNavigate()
  const state = useLocation()
  const months = state?.state?.months
  const isAdult = months > 24
  const isPediatric = months <= 24
  const isFemale = state?.state?.patient_sex?.toUpperCase() === "FEMALE"
  const isMale = state?.state?.patient_sex?.toUpperCase() === "MALE"
  const vitalSavedRef = useRef(false)
  const [isLoading, setIsLoading] = useState(false)
  const [bpError, setBpError] = useState("")
  const [suggestComplaint, setSuggestComplaint] = useState([])
  const [selectedDoctor, setSelectedDoctor] = useState(null)
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [patientDetails, setPatientDetails] = useState(null)
  const [addVital, setAddVital] = useState({
    weight: "",
    weight_in_lb: "",
    height: "",
    height_in_ft: "",
    height_in_inch: "",
    bmi: "",
    ibw: "",
    waist: "",
    hip: "",
    whr: "",
    temperature: "",
    bp: "",
    heart_rate: "",
    respiratory_rate: "",
    oxygen_saturation: "",
    blood_type: "",
    left_eye_wo_correction: "",
    right_eye_wo_correction: "",
    left_eye_w_correction: "",
    right_eye_w_correction: "",

    length: "",
    head_circumference: "",
    skinfold_thickness: "",
    body_circumference_waist: "",
    hip_in_cm: "",
    limbs: "",
    muac: "",

    chief_complaint: "",
    has_complaint_2: "0",
    complaint_2: "",
    has_complaint_3: "0",
    complaint_3: "",
    has_complaint_4: "0",
    complaint_4: "",
    has_complaint_5: "0",
    complaint_5: "",
    has_complaint_6: "0",
    complaint_6: "",
    has_complaint_8: "0",
    complaint_8: "",
    menarche_period: "",
    last_mens_period: "",
    period_duration: "",
    mens_interval: "",
    pads_per_day: "",
    onset_sex_ic: "",
    birth_ctrl_method: "",
    is_menopause: "",
    menopause_age: "",
    preg_count: "",
    general_survey: "",
    general_survey_remarks: "",
  })

  const bpPattern = /^\d{2,3}\/\d{2,3}$/

  const handleAddComplaintChange = (e, search) => {
    if (search === "chief_complaint") {
      const upperValue = e?.value ? e.value.toUpperCase() : ""
      setAddVital((prevState) => ({
        ...prevState,
        chief_complaint: upperValue,
      }))
    } else {
      const { name, value } = e.target
      setAddVital((prevState) => ({
        ...prevState,
        [name]: value,
      }))
    }
  }

  const handleBpBlur = () => {
    if (!addVital.bp) {
      setBpError("")
      return
    }

    let formatted = addVital.bp
    if (!formatted.includes("/")) {
      if (formatted.length === 4) {
        formatted = `${formatted.slice(0, 2)}/${formatted.slice(2)}`
      } else if (formatted.length > 4) {
        formatted = `${formatted.slice(0, 3)}/${formatted.slice(3)}`
      }
    }

    setAddVital((prevState) => ({
      ...prevState,
      bp: formatted,
    }))

    if (!bpPattern.test(formatted)) {
      setBpError("Invalid format. Example: 120/80")
    } else {
      setBpError("")
    }
  }

  const handleAddChange = (e) => {
    const { name } = e.target
    let { value } = e.target

    if (["menarche_period", "onset_sex_ic", "menopause_age"].includes(name)) {
      value = value.replace(/\D/g, "").slice(0, 2)
      e.target.value = value
    }

    if (name === "height") {
      if (value.trim() === "") {
        setAddVital((prevState) => ({
          ...prevState,
          height_in_ft: "",
          height: "",
          height_in_inch: "",
        }))
        return
      }

      const cm = parseFloat(value)
      if (!isNaN(cm)) {
        const totalInches = cm / 2.54
        const feet = Math.floor(totalInches / 12)
        const inches = Math.round(totalInches % 12)

        const heightInFt = `${feet}'${inches}"`

        setAddVital((prevState) => ({
          ...prevState,
          height: value,
          height_in_inch: Math.round(totalInches),
          height_in_ft: heightInFt,
        }))
      }
    } else if (name === "weight") {
      if (value.trim() === "") {
        setAddVital((prevState) => ({
          ...prevState,
          weight: "",
          weight_in_lb: "",
        }))
        return
      }

      const kg = parseFloat(value)
      if (!isNaN(kg)) {
        const lb = (kg * 2.20462).toFixed(2)
        setAddVital((prevState) => ({
          ...prevState,
          weight: value,
          weight_in_lb: lb,
        }))
      }
    } else if (name === "hip") {
      if (value.trim() === "") {
        setAddVital((prevState) => ({
          ...prevState,
          hip: "",
          hip_in_cm: "",
        }))
        return
      }

      const inches = parseFloat(value)
      if (!isNaN(inches)) {
        const cm = (inches * 2.54).toFixed(2)
        setAddVital((prevState) => ({
          ...prevState,
          hip: value,
          hip_in_cm: cm,
        }))
      }
    } else if (name === "hip_in_cm") {
      if (value.trim() === "") {
        setAddVital((prevState) => ({
          ...prevState,
          hip: "",
          hip_in_cm: "",
        }))
        return
      }

      const cm = parseFloat(value)
      if (!isNaN(cm)) {
        const inches = (cm / 2.54).toFixed(2)
        setAddVital((prevState) => ({
          ...prevState,
          hip: inches,
          hip_in_cm: value,
        }))
      }
    } else if (name === "bp") {
      const digits = value.replace(/\D/g, "").slice(0, 6)
      const previousDigits = addVital.bp.replace(/\D/g, "")
      const removedSlash =
        addVital.bp.includes("/") &&
        !value.includes("/") &&
        digits.length === previousDigits.length
      let cleaned = digits

      if (!removedSlash) {
        if (digits.length === 2) {
          cleaned = `${digits}/`
        } else if (digits.length === 3) {
          cleaned = `${digits}/`
        } else if (digits.length === 4) {
          cleaned = `${digits.slice(0, 2)}/${digits.slice(2)}`
        } else if (digits.length > 4) {
          cleaned = `${digits.slice(0, 3)}/${digits.slice(3)}`
        }
      }

      setBpError("")
      setAddVital((prevState) => ({
        ...prevState,
        bp: cleaned,
      }))
    } else if (name === "general_survey") {
      setAddVital((prevState) => ({
        ...prevState,
        general_survey: value,
        general_survey_remarks:
          value === "Altered Sensorium" ? "ALTERED SENSORIUM" : "",
      }))
    } else if (/^has_complaint_\d+$/.test(name) && value === "0") {
      const complaintKey = name.replace("has_", "")
      setAddVital((prevState) => ({
        ...prevState,
        [name]: value,
        [complaintKey]: "",
      }))
    } else {
      setAddVital((prevState) => ({
        ...prevState,
        [name]: value,
      }))
    }
  }

  async function handleNext() {
    setIsLoading(true)

    let response
    if (vitalSavedRef.current) {
      response = { status: 200 }
    } else {
      response = await _createVitalSign(false, true)
      if (response?.status === 200) {
        vitalSavedRef.current = true
      }
    }

    if (response?.status === 200) {
      if (isFemale) {
        const obResponse = await createOBProfile(addVital, id.id)
        if (obResponse?.status !== 200) {
          toast.error(
            obResponse?.response?.data?.messages?.error_messages[0] ??
              "An error occurred saving OB profile"
          )
          setIsLoading(false)
          return
        }
      }

      const navState = {
        patient_id: id?.id,
        patient_sex: state?.state?.patient_sex,
        is_fpe: state?.state?.is_fpe,
        months: state?.state?.months,
        doctor_id: selectedDoctor,
        vitals: addVital,
        fromConsult: state?.state?.fromConsult || false,
        queue_id: state?.state?.queueId || "",
        queue_no: state?.state?.queueNo || "",
        fromRegisterPatient: state?.state?.fromRegisterPatient || false,
      }

      if (state?.state?.fromConsult == true) {
        navigate(`/patients/profile/${id?.id}`, {
          replace: true,
          state: navState,
        })
      } else if (state?.state?.months <= 24) {
        navigate(`/patients/physical-examination-yakap/${id?.id}`, {
          replace: true,
          state: navState,
        })
      } else {
        navigate(`/patients/ncdq/${id?.id}`, {
          replace: true,
          state: navState,
        })
      }
    } else {
      setIsLoading(false)
    }
  }

  async function _createVitalSign(navigateToProfile, skipAutoProfileRedirect = false) {
    const response = await createVital(
      { ...addVital, date: selectedDate ? selectedDate.format("YYYY-MM-DD") : "" },
      id.id
    )
    if (response.status !== 200) {
      toast.error(
        response?.response?.data?.messages?.error_messages[0] ??
          "An error occured"
      )
    } else {
      toast.success(response.data.message ?? "Success")

      if (!skipAutoProfileRedirect && state?.state?.fromPhilhealthCheck) {
        setTimeout(() => {
          navigate("/patients/profile/" + id.id, {
            state: {
              fromConsult: false,
              fromRegisterPatient: true,
              fromPhilhealthCheck: true,
            },
          })
          localStorage.setItem("isCheck", JSON.stringify(true))
          localStorage.setItem("isTrue", JSON.stringify(true))
          localStorage.setItem("vitalSignLab", JSON.stringify("vital_signs"))
          localStorage.setItem(
            "medicalHistory",
            JSON.stringify("medical_history")
          )
          localStorage.setItem(
            "chiefComplaint",
            JSON.stringify(addVital?.chief_complaint)
          )
        }, 2000)
      } else if (navigateToProfile) {
        setTimeout(() => {
          navigate("/patients/profile/" + id.id, {
            state: {
              fromConsult: state?.state?.fromConsult || false,
              queue_id: state?.state?.queueId || "",
              queue_no: state?.state?.queueNo || "",
              fromRegisterPatient: state?.state?.fromRegisterPatient || false,
            },
          })
          localStorage.setItem("isCheck", JSON.stringify(true))
          localStorage.setItem("isTrue", JSON.stringify(true))
          localStorage.setItem("vitalSignLab", JSON.stringify("vital_signs"))
          localStorage.setItem(
            "medicalHistory",
            JSON.stringify("medical_history")
          )
          localStorage.setItem(
            "chiefComplaint",
            JSON.stringify(addVital?.chief_complaint)
          )
        }, 2000)
      }
    }
    return response
  }

  async function fetchAutoSuggestComplaint() {
    const response = await getAutoSuggestTherapeutics({
      table: "assessment",
      field: "complaint",
    })
    if (response.data) {
      setSuggestComplaint(
        response?.data?.data
          ?.filter((data) => data !== null && data !== undefined && data !== "")
          ?.map((data) => ({ label: data, value: data }))
      )
    } else {
      setSuggestComplaint([])
    }
  }

  async function fetchPatientDetails() {
    const response = await getPatientID(id.id, "", "")
    if (response?.data) {
      const patient = response?.data?.data[0]
      setPatientDetails(patient)
    } else {
      setPatientDetails(null)
    }
  }

  useEffect(() => {
    var inchOver5feet = 0
    var ibw = 0
    var vitalIbw = ""

    if (!addVital.height) {
      vitalIbw = ""
    } else {
      inchOver5feet = parseFloat(addVital.height) / 2.54 - 60

      if (isMale) {
        ibw = 50 + 2.3 * inchOver5feet
      } else {
        ibw = 45.5 + 2.3 * inchOver5feet
      }
      vitalIbw = ibw.toFixed(2)
    }

    if (!addVital.weight || !addVital.height) {
      setAddVital((prevState) => ({
        ...prevState,
        ibw: vitalIbw,
        bmi: "",
      }))
    } else {
      setAddVital((prevState) => ({
        ...prevState,
        ibw: vitalIbw,
        bmi:
          parseFloat(
            addVital?.weight /
              ((addVital?.height / 100) * (addVital?.height / 100))
          ).toFixed(2) || "",
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addVital.height, addVital.weight])

  useEffect(() => {
    const hipForRatio = isPediatric ? addVital.hip_in_cm : addVital.hip
    if (!addVital.waist || !hipForRatio) {
      setAddVital((prevState) => {
        return {
          ...prevState,
          whr: "",
        }
      })
    } else {
      setAddVital((prevState) => {
        return {
          ...prevState,
          whr: parseFloat(addVital.waist / hipForRatio).toFixed(2),
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addVital.waist, addVital.hip, addVital.hip_in_cm])

  useEffect(() => {
    fetchAutoSuggestComplaint()
    fetchPatientDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (role === "director" || role === "pcp" || role === "specialist") {
      setSelectedDoctor(userId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role])

  const nextDisabled = isPediatric
    ? !addVital?.general_survey || !addVital?.length || !addVital?.muac
    : isAdult
    ? !addVital?.general_survey ||
      !addVital?.height ||
      !addVital?.bmi ||
      !addVital?.temperature ||
      !bpPattern.test(addVital?.bp || "") ||
      !!bpError ||
      !addVital?.heart_rate ||
      !addVital?.respiratory_rate
    : true

  return (
    <div className="App">
      <Sidebar />
      <div className="profile-container col p-5 pt-4">
        <div className="ml-7 col font-big font-bold d-flex align-items-center gap-3">
          Add Vital Signs
          <DatePicker format={DATE_FORMAT}
            size="small"
            placeholder="Select Date"
            value={selectedDate}
            style={{ fontWeight: "normal", width: "170px", height: "40px" }}
            onChange={(date, dateString) => {
              setSelectedDate(date)
            }}
          />
        </div>
        {patientDetails && (
          <div className="ml-7 mt-2 mb-4 p-3 bg-light rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
            <div className="row">
              <div className="col-md-6">
                <p className="mb-1">
                  <strong>Patient Name:</strong> {patientDetails?.last_name}, {patientDetails?.first_name} {patientDetails?.middle_name || ''}
                </p>
              </div>
              <div className="col-md-3">
                <p className="mb-1">
                  <strong>Age:</strong> {patientDetails?.age || 'N/A'}
                </p>
              </div>
              <div className="col-md-3">
                <p className="mb-1">
                  <strong>Date of Birth:</strong> {patientDetails?.birthdate ? formatDate(patientDetails.birthdate) : "N/A"}
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="ml-7">
          <div className="row">
            <div className="col-sm-11">
              <div className="row mt-3"></div>
              {isAdult && (
                <>
                  <div className="row">
                    <div className="col-sm-3">
                      <NumberInput
                        id="weight"
                        name="weight"
                        formLabel={`WEIGHT (kg)`}
                        size={`sm`}
                        onChange={handleAddChange}
                        required={true}
                      />
                    </div>
                    <div className="col-sm-3">
                      <NumberInput
                        id="weight_in_lb"
                        name="weight_in_lb"
                        formLabel={`WEIGHT (lb)`}
                        value={addVital.weight_in_lb}
                        size={`sm`}
                        onChange={handleAddChange}
                        errorMessage="This is an error"
                        disabled={true}
                      />
                    </div>
                    <div className="col-sm-3">
                      <NumberInput
                        id="height"
                        name="height"
                        formLabel={`HEIGHT (cm)`}
                        value={addVital.height}
                        size={`sm`}
                        onChange={handleAddChange}
                        required={true}
                      />
                    </div>
                    <div className="col-sm-3">
                      <NumberInput
                        id="height_in_inch"
                        name="height_in_inch"
                        formLabel={`HEIGHT (in)`}
                        value={addVital.height_in_inch}
                        size={`sm`}
                        onChange={handleAddChange}
                        errorMessage="This is an error"
                        disabled={true}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-3">
                      <NumberInput
                        id="bmi"
                        name="bmi"
                        formLabel={`BMI`}
                        value={addVital.bmi}
                        size={`sm`}
                        disabled={true}
                        required={true}
                        onChange={handleAddChange}
                      />
                    </div>
                    <div className="col-sm-3">
                      <NumberInput
                        id="ibw"
                        name="ibw"
                        formLabel={`IBW (kg)`}
                        size={`sm`}
                        value={addVital.ibw}
                        onChange={handleAddChange}
                        disabled={true}
                      />
                    </div>
                    <div className="col-sm-3">
                      <NumberInput
                        id="whr"
                        name="whr"
                        formLabel={`WHR`}
                        size={`sm`}
                        onChange={handleAddChange}
                        value={addVital.whr}
                      />
                    </div>
                    <div className="col-sm-3">
                      <NumberInput
                        id="temperature"
                        name="temperature"
                        formLabel={`TEMPERATURE (°C)`}
                        size={`sm`}
                        onChange={handleAddChange}
                        required={true}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-3">
                      <NumberInput
                        id="bp"
                        name="bp"
                        formLabel={`BLOOD PRESSURE (mmHg)`}
                        size={`sm`}
                        value={addVital.bp}
                        onChange={handleAddChange}
                        onBlur={handleBpBlur}
                        required={true}
                      />
                      {bpError && (
                        <small style={{ color: "red" }}>{bpError}</small>
                      )}
                    </div>
                    <div className="col-sm-3">
                      <NumberInput
                        id="heart_rate"
                        name="heart_rate"
                        formLabel={`HEART RATE (bpm)`}
                        size={`sm`}
                        onChange={handleAddChange}
                        required={true}
                      />
                    </div>
                    <div className="col-sm-3">
                      <NumberInput
                        id="respiratory_rate"
                        name="respiratory_rate"
                        formLabel={`RESPIRATORY RATE`}
                        size={`sm`}
                        onChange={handleAddChange}
                        required={true}
                      />
                    </div>
                    <div className="col-sm-3">
                      <NumberInput
                        id="oxygen_saturation"
                        name="oxygen_saturation"
                        formLabel={`OXYGEN SATURATION`}
                        size={`sm`}
                        onChange={handleAddChange}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-3">
                      <NumberInput
                        id="left_eye_w_correction"
                        name="left_eye_w_correction"
                        formLabel={`LEFT EYE VISION (w/ correction)`}
                        size={`sm`}
                        onChange={handleAddChange}
                      />
                    </div>
                    <div className="col-sm-3">
                      <NumberInput
                        id="right_eye_w_correction"
                        name="right_eye_w_correction"
                        formLabel={`RIGHT EYE VISION (w/ correction)`}
                        size={`sm`}
                        onChange={handleAddChange}
                      />
                    </div>
                    <div className="col-sm-3">
                      <NumberInput
                        id="left_eye_wo_correction"
                        name="left_eye_wo_correction"
                        formLabel="LEFT EYE VISION (w/o correction)"
                        size="sm"
                        min={0}
                        max={20}
                        onChange={handleAddChange}
                      />
                      {addVital.left_eye_wo_correction !== "" &&
                        addVital.left_eye_wo_correction != null &&
                        (!Number.isInteger(Number(addVital.left_eye_wo_correction)) ||
                          Number(addVital.left_eye_wo_correction) < 0 ||
                          Number(addVital.left_eye_wo_correction) > 20) && (
                          <div className="text-danger small mt-1">
                            Left eye vision must be a whole number between 0 and 20.
                          </div>
                        )}
                    </div>

                    <div className="col-sm-3">
                      <NumberInput
                        id="right_eye_wo_correction"
                        name="right_eye_wo_correction"
                        formLabel="RIGHT EYE VISION (w/o correction)"
                        size="sm"
                        min={0}
                        max={20}
                        onChange={handleAddChange}
                      />
                      {addVital.right_eye_wo_correction !== "" &&
                        addVital.right_eye_wo_correction != null &&
                        (!Number.isInteger(Number(addVital.right_eye_wo_correction)) ||
                          Number(addVital.right_eye_wo_correction) < 0 ||
                          Number(addVital.right_eye_wo_correction) > 20) && (
                          <div className="text-danger small mt-1">
                            Right eye vision must be a whole number between 0 and 20.
                          </div>
                        )}
                    </div>
                  </div>
                </>
              )}
              {isPediatric && (
                <>
                  {" "}
                  <label
                    style={{
                      fontWeight: "bold",
                      textDecoration: "underline",
                    }}
                  >
                    Pedriatic Client aged 0-24
                  </label>
                  <div className="row mt-3">
                    <div className="col">
                      <NumberInput
                        id="weight"
                        name="weight"
                        formLabel={`WEIGHT (kg)`}
                        size={`sm`}
                        onChange={handleAddChange}
                      />
                    </div>
                    <div className="col">
                      <NumberInput
                        id="weight_in_lb"
                        name="weight_in_lb"
                        formLabel={`WEIGHT (lb)`}
                        value={addVital.weight_in_lb}
                        size={`sm`}
                        onChange={handleAddChange}
                        errorMessage="This is an error"
                        disabled={true}
                      />
                    </div>
                    <div className="col">
                      <NumberInput
                        id="length"
                        name="length"
                        formLabel={`LENGTH (cm)`}
                        size={`sm`}
                        onChange={handleAddChange}
                        required={true}
                        errorMessage="This is an error"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <NumberInput
                        id="head_circumference"
                        name="head_circumference"
                        formLabel={`HEAD CIRCUMFERENCE (cm)`}
                        size={`sm`}
                        onChange={handleAddChange}
                        errorMessage="This is an error"
                      />
                    </div>
                    <div className="col">
                      <NumberInput
                        id="skinfold_thickness"
                        name="skinfold_thickness"
                        formLabel={`SKINFOLD THICKNESS (cm)`}
                        size={`sm`}
                        onChange={handleAddChange}
                        errorMessage="This is an error"
                      />
                    </div>
                    <div className="col">
                      <NumberInput
                        id="waist"
                        name="waist"
                        formLabel={`BODY CIRCUMFERENCE WAIST (cm)`}
                        size={`sm`}
                        onChange={handleAddChange}
                        errorMessage="This is an error"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <NumberInput
                        id="hip_in_cm"
                        name="hip_in_cm"
                        formLabel={`HIP (cm)`}
                        size={`sm`}
                        onChange={handleAddChange}
                        errorMessage="This is an error"
                      />
                    </div>
                    <div className="col">
                      <NumberInput
                        id="limbs"
                        name="limbs"
                        formLabel={`LIMBS (cm)`}
                        size={`sm`}
                        onChange={handleAddChange}
                        errorMessage="This is an error"
                      />
                    </div>
                    <div className="col">
                      <NumberInput
                        id="muac"
                        name="muac"
                        formLabel={`MIDDLE & UPPER ARM CIRCUMFERENCE (cm)`}
                        size={`sm`}
                        onChange={handleAddChange}
                        required={true}
                        errorMessage="This is an error"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-4">
                      <NumberInput
                        id="temperature"
                        name="temperature"
                        formLabel={`TEMPERATURE (°C)`}
                        size={`sm`}
                        onChange={handleAddChange}
                      />
                    </div>
                  </div>
                </>
              )}
              {!state?.state?.fromConsult && (
                <>
                  <div className="row mt-3">
                    <div className="col">
                      <label className="form-label">Blood Type (as available)</label>
                      <Radio.Group
                        className="w-full mt-2"
                        value={addVital?.blood_type}
                        onChange={(e) =>
                          handleAddChange({
                            target: { name: "blood_type", value: e.target.value },
                          })
                        }
                      >
                        <Radio value="A+">A+</Radio>
                        <Radio value="B+">B+</Radio>
                        <Radio value="AB+">AB+</Radio>
                        <Radio value="O+">O+</Radio>
                        <Radio value="A-">A-</Radio>
                        <Radio value="B-">B-</Radio>
                        <Radio value="AB-">AB-</Radio>
                        <Radio value="O-">O-</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                  <hr />
                  <div className="row mt-3">
                    <div className="col">
                      <CreateSelectField
                        id={"complaint"}
                        errorMessage="Required"
                        className="dropsearch-filter"
                        name="complaint"
                        size={`sm`}
                        formLabel={"Illness History"}
                        options={suggestComplaint}
                        value={
                          addVital?.chief_complaint
                            ? { label: addVital.chief_complaint, value: addVital.chief_complaint }
                            : null
                        }
                        isMessage={true}
                        onChange={(e) =>
                          handleAddComplaintChange(e, "chief_complaint")
                        }
                      />
                    </div>
                  </div>
                  <div className="row mt-3">
                    <div className="col">
                      <p className="form-label align-left">
                        2. Do you experience any of the following: loss of appetite,
                        lack of sleep, unexplained weight loss, feeling
                        down/depressed, fever, headache, memory loss, blurring of
                        vision, or hearing loss?
                      </p>
                      <Radio.Group
                        className="w-full mt-2"
                        name="has_complaint_2"
                        value={addVital?.has_complaint_2}
                        onChange={handleAddChange}
                      >
                        <Radio value="1">Yes</Radio>
                        <Radio value="0">No</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                  {addVital?.has_complaint_2 === "1" && (
                    <>
                      <div className="row mt-3">
                        <div className="col">
                          <TextArea
                            required={true}
                            id="complaint_2"
                            name="complaint_2"
                            formLabel={`If yes, please explain:`}
                            value={addVital?.complaint_2}
                            onChange={handleAddChange}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="row mt-3">
                    <div className="col">
                      <p className="form-label align-left">
                        3. Do you experience any of the following: cough/colds,
                        chest pain, palpitations, or difficulty in breathing?
                      </p>
                      <Radio.Group
                        className="w-full mt-2"
                        name="has_complaint_3"
                        value={addVital?.has_complaint_3}
                        onChange={handleAddChange}
                      >
                        <Radio value="1">Yes</Radio>
                        <Radio value="0">No</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                  {addVital?.has_complaint_3 === "1" && (
                    <>
                      <div className="row mt-3">
                        <div className="col">
                          <TextArea
                            required={true}
                            id="complaint_3"
                            name="complaint_3"
                            formLabel={`If yes, please explain:`}
                            value={addVital?.complaint_3}
                            onChange={handleAddChange}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="row mt-3">
                    <div className="col">
                      <p className="form-label align-left">
                        4. Do you experience any of the following: abdominal pain,
                        vomiting, change in bowel movement, rectal bleeding, or
                        bloody/tarry stools?
                      </p>
                      <Radio.Group
                        className="w-full mt-2"
                        name="has_complaint_4"
                        value={addVital?.has_complaint_4}
                        onChange={handleAddChange}
                      >
                        <Radio value="1">Yes</Radio>
                        <Radio value="0">No</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                  {addVital?.has_complaint_4 === "1" && (
                    <>
                      <div className="row mt-3">
                        <div className="col">
                          <TextArea
                            required={true}
                            id="complaint_4"
                            name="complaint_4"
                            formLabel={`If yes, please explain:`}
                            value={addVital?.complaint_4}
                            onChange={handleAddChange}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="row mt-3">
                    <div className="col">
                      <p className="form-label align-left">
                        5. Do you experience any of the following: frequent
                        urination, frequent eating, frequent intake of fluids?
                      </p>
                      <Radio.Group
                        className="w-full mt-2"
                        name="has_complaint_5"
                        value={addVital?.has_complaint_5}
                        onChange={handleAddChange}
                      >
                        <Radio value="1">Yes</Radio>
                        <Radio value="0">No</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                  {addVital?.has_complaint_5 === "1" && (
                    <>
                      <div className="row mt-3">
                        <div className="col">
                          <TextArea
                            required={true}
                            id="complaint_5"
                            name="complaint_5"
                            formLabel={`If yes, please explain:`}
                            value={addVital?.complaint_5}
                            onChange={handleAddChange}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="row mt-3">
                    <div className="col">
                      <p className="form-label align-left">
                        6. For male and female, do you experience any of the
                        following: pain or discomfort on urination, frequency of
                        urination, dribbling of urine, pain during/after sex, blood
                        in urine, or foul-smelling genital discharge?
                      </p>
                      <Radio.Group
                        className="w-full mt-2"
                        name="has_complaint_6"
                        value={addVital?.has_complaint_6}
                        onChange={handleAddChange}
                      >
                        <Radio value="1">Yes</Radio>
                        <Radio value="0">No</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                  {addVital?.has_complaint_6 === "1" && (
                    <>
                      <div className="row mt-3">
                        <div className="col">
                          <TextArea
                            required={true}
                            id="complaint_6"
                            name="complaint_6"
                            formLabel={`If yes, please explain:`}
                            value={addVital?.complaint_6}
                            onChange={handleAddChange}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {isFemale && (
                    <div className="row mt-3">
                      <div className="col-12">
                        <p>7. For females only,</p>
                      </div>
                      <div className="col-sm-3">
                        <NumberInput
                          id="menarche_period"
                          name="menarche_period"
                          formLabel={`a. Age of First Menstruation (Menarche)`}
                          size={`sm`}
                          maxLength={2}
                          onChange={handleAddChange}
                        />
                      </div>
                      <div className="col-sm-3">
                        <DateField
                          id="last_mens_period"
                          name="last_mens_period"
                          formLabel={`b. Date of Last Menstrual Period`}
                          size={`sm`}
                          onChange={handleAddChange}
                          errorMessage="This is an error"
                        />
                      </div>
                      <div className="col-sm-3">
                        <NumberInput
                          id="period_duration"
                          name="period_duration"
                          formLabel={`c. Duration of Menstrual Period (days)`}
                          size={`sm`}
                          onChange={handleAddChange}
                        />
                      </div>
                      <div className="col-sm-3">
                        <NumberInput
                          id="mens_interval"
                          name="mens_interval"
                          formLabel={`d. Interval/Cycle of Menstruation (days)`}
                          size={`sm`}
                          onChange={handleAddChange}
                        />
                      </div>
                      <div className="row mt-2">
                        <div className="col-sm-3">
                          <NumberInput
                            id="pads_per_day"
                            name="pads_per_day"
                            formLabel={`e. Number of Pads/Napkins per Day`}
                            size={`sm`}
                            onChange={handleAddChange}
                          />
                        </div>
                        <div className="col-sm-3">
                          <NumberInput
                            id="onset_sex_ic"
                            name="onset_sex_ic"
                            formLabel={`f. Onset of Sexual Intercourse (Age)`}
                            size={`sm`}
                            maxLength={2}
                            onChange={handleAddChange}
                          />
                        </div>
                        <div className="col-sm-3">
                          <TextField
                            id="birth_ctrl_method"
                            name="birth_ctrl_method"
                            formLabel={`g. Birth Control Method Used`}
                            size={`sm`}
                            maxLength={21}
                            onChange={handleAddChange}
                          />
                        </div>
                        <div className="col-sm-3">
                          <label className="form-label">h. Is Menopause?</label>
                          <div className="d-flex align-items-center mt-2">
                            <Radio.Group
                              name="is_menopause"
                              value={addVital?.is_menopause}
                              onChange={handleAddChange}
                            >
                              <Radio value="Y">Yes</Radio>
                              <Radio value="N">No</Radio>
                            </Radio.Group>
                          </div>
                        </div>
                      </div>
                      {addVital?.is_menopause === "Y" && (
                        <div className="row mt-2">
                          <div className="col-sm-3">
                            <NumberInput
                              id="menopause_age"
                              name="menopause_age"
                              formLabel={`i. Age of Menopause`}
                              size={`sm`}
                              maxLength={2}
                              onChange={handleAddChange}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="row mt-3">
                    <div className="col">
                      <p className="form-label align-left">
                        {isFemale ? "8" : "7"}. Do you experience any of the following: muscle spasm,
                        tremors, weakness; muscle/joint pain stiffness,
                        limitation of movement?
                      </p>
                      <Radio.Group
                        className="w-full mt-2"
                        name="has_complaint_8"
                        value={addVital?.has_complaint_8}
                        onChange={handleAddChange}
                      >
                        <Radio value="1">Yes</Radio>
                        <Radio value="0">No</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                  {addVital?.has_complaint_8 === "1" && (
                    <>
                      <div className="row mt-3">
                        <div className="col">
                          <TextArea
                            required={true}
                            id="complaint_8"
                            name="complaint_8"
                            formLabel={`If yes, please explain:`}
                            value={addVital?.complaint_8}
                            onChange={handleAddChange}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              <hr className="mt-3" />
              <label
                style={{
                  fontWeight: "bold",
                  textDecoration: "underline",
                }}
              >
                General Survey
              </label>
              <div className="row">
                <div className="col">
                  <Radio.Group
                    className="w-full mt-2"
                    name="general_survey"
                    value={addVital?.general_survey}
                    onChange={handleAddChange}
                  >
                    <Radio value="Awake and alert">Awake and alert</Radio>
                    <Radio value="Altered Sensorium">Altered Sensorium</Radio>
                  </Radio.Group>
                </div>
                {addVital?.general_survey === "Altered Sensorium" && (
                  <div className="col-sm-4">
                    <label className="form-label" htmlFor="general_survey_remarks">
                      REMARKS
                    </label>
                    <input
                      id="general_survey_remarks"
                      name="general_survey_remarks"
                      type="text"
                      className="form-control form-control-sm"
                      value={addVital?.general_survey_remarks || ""}
                      onChange={handleAddChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="row justify-content-end mt-5 mb-3">
            <div className="col-2">
              <BtnAdd
                label={"Next"}
                size={"sm"}
                onClick={handleNext}
                disabled={nextDisabled}
                isClicked={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewPatientVitalSigns